import { useEffect, useState } from "react";
import { Search, KeyRound, X, Copy, MessageCircle, Check, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatARS } from "@/lib/api";
import { SELLERS, findSellerByCode, buildNewCustomerNotice } from "@/lib/sellers";

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [resetting, setResetting] = useState(null);
  const [changingSeller, setChangingSeller] = useState(null);

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => toast.error("No se pudieron cargar los clientes")); }, []);

  const changeSeller = async (c, code) => {
    if (code === c.seller_code) return;
    const seller = SELLERS.find((s) => s.code === code);
    if (!window.confirm(`¿Reasignar a ${c.name} al vendedor ${code} - ${seller?.name}? Sus próximos pedidos irán a ese vendedor.`)) return;
    setChangingSeller(c.id);
    try {
      const { data } = await api.patch(`/customers/${c.id}/seller`, { seller_code: code });
      setCustomers((arr) => arr.map((x) => (x.id === c.id ? { ...x, ...data } : x)));
      toast.success(`${c.name} ahora es atendido por ${data.seller_name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo cambiar el vendedor");
    } finally { setChangingSeller(null); }
  };

  const term = q.trim().toLowerCase();
  const filtered = term
    ? customers.filter((c) => [c.name, c.email, c.city, c.cuit, c.seller_name, c.seller_code].some((v) => (v || "").toLowerCase().includes(term)))
    : customers;

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, email, ciudad, CUIT o vendedor..." className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 outline-none focus:border-blue-400 text-sm" data-testid="customers-search" />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} cliente(s)</span>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3 hidden md:table-cell">Vendedor</th>
              <th className="text-left p-3 hidden lg:table-cell">Ubicación / CUIT</th>
              <th className="text-right p-3">Pedidos</th>
              <th className="text-left p-3 hidden sm:table-cell">Alta</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                <td className="p-3">
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.email}{c.phone && ` · ${c.phone}`}</div>
                </td>
                <td className="p-3 hidden md:table-cell">
                  <select
                    value={c.seller_code}
                    onChange={(e) => changeSeller(c, e.target.value)}
                    disabled={changingSeller === c.id}
                    className="px-2.5 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-xs font-medium outline-none focus:border-blue-400 cursor-pointer disabled:opacity-50"
                    data-testid={`customer-seller-select-${c.id}`}
                  >
                    {SELLERS.map((s) => <option key={s.code} value={s.code}>{`${s.code} · ${s.name}`}</option>)}
                  </select>
                  {c.seller_changed_at && <div className="text-[10px] text-slate-400 mt-1">Cambiado {new Date(c.seller_changed_at).toLocaleDateString("es-AR")}</div>}
                </td>
                <td className="p-3 hidden lg:table-cell text-xs text-slate-600">{c.city || "—"}{c.cuit && <div className="font-mono text-slate-500">CUIT {c.cuit}</div>}</td>
                <td className="p-3 text-right" data-testid={`customer-orders-${c.id}`}>
                  {c.orders_count > 0 ? (
                    <>
                      <div className="font-semibold text-slate-900">{c.orders_count} pedido{c.orders_count === 1 ? "" : "s"}</div>
                      <div className="text-xs text-green-700 font-semibold">{formatARS(c.orders_total)}</div>
                      {c.last_order_at && <div className="text-[10px] text-slate-400">último {new Date(c.last_order_at).toLocaleDateString("es-AR")}</div>}
                    </>
                  ) : (
                    <span className="text-xs text-slate-400">Sin pedidos</span>
                  )}
                </td>
                <td className="p-3 hidden sm:table-cell text-xs text-slate-500">
                  {new Date(c.created_at).toLocaleDateString("es-AR")}
                  {c.password_changed_at && <div className="text-[10px] text-amber-600">Clave reseteada {new Date(c.password_changed_at).toLocaleDateString("es-AR")}</div>}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex flex-wrap justify-end gap-1.5">
                    {(() => {
                      const notice = buildNewCustomerNotice(c, findSellerByCode(c.seller_code), { fromAdmin: true });
                      return notice ? (
                        <a href={notice.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-green-700 border border-green-200 hover:bg-green-50 transition-colors" title={`Avisar a ${c.seller_name} por WhatsApp`} data-testid={`notify-seller-${c.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" /> Avisar al vendedor
                        </a>
                      ) : null;
                    })()}
                    <button onClick={() => setResetting(c)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-amber-700 border border-amber-200 hover:bg-amber-50 transition-colors" data-testid={`reset-password-${c.id}`}>
                      <KeyRound className="w-3.5 h-3.5" /> Resetear contraseña
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="p-10 text-center text-slate-500">{customers.length ? "Sin resultados para la búsqueda." : "Todavía no hay clientes registrados."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {resetting && <ResetPasswordModal customer={resetting} onClose={() => setResetting(null)} onDone={(updated) => setCustomers((arr) => arr.map((c) => (c.id === updated.id ? { ...c, password_changed_at: updated.reset_at } : c)))} />}
    </div>
  );
}

function ResetPasswordModal({ customer, onClose, onDone }) {
  const [mode, setMode] = useState("auto"); // auto | manual
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "manual" && password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/customers/${customer.id}/reset-password`, mode === "manual" ? { new_password: password } : {});
      setResult(data);
      onDone(data);
      toast.success("Contraseña restablecida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo restablecer la contraseña");
    } finally { setSaving(false); }
  };

  const waText = result ? `Hola ${result.name}! Te restablecimos la contraseña de tu cuenta en Produlimp BV.\nEmail: ${result.email}\nContraseña nueva: ${result.temp_password}\nPodés cambiarla después desde tu cuenta.` : "";
  const waPhone = (result?.phone || "").replace(/\D/g, "");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="reset-password-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> Resetear contraseña</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="reset-modal-close"><X className="w-4 h-4" /></button>
        </div>
        {!result ? (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="bg-slate-50 rounded-xl p-3 text-sm">
              <div className="font-semibold text-slate-900">{customer.name}</div>
              <div className="text-xs text-slate-500">{customer.email}</div>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="mode" checked={mode === "auto"} onChange={() => setMode("auto")} className="mt-0.5" data-testid="reset-mode-auto" />
                <span><strong>Generar automáticamente</strong> una contraseña temporal de 8 caracteres</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="mode" checked={mode === "manual"} onChange={() => setMode("manual")} className="mt-0.5" data-testid="reset-mode-manual" />
                <span><strong>Escribir una contraseña</strong> (mínimo 6 caracteres)</span>
              </label>
              {mode === "manual" && (
                <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required placeholder="Nueva contraseña" className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none ml-6 max-w-[calc(100%-1.5rem)]" data-testid="reset-password-input" />
              )}
            </div>
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3">
              <ShieldAlert className="w-4 h-4 flex-shrink-0 text-amber-600" />
              <span>El cliente va a tener que ingresar con la nueva contraseña; sus sesiones abiertas se cierran automáticamente.</span>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg hover:bg-slate-100">Cancelar</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="reset-password-submit">{saving ? "Guardando..." : "Restablecer"}</button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-4" data-testid="reset-password-result">
            <div className="flex items-center gap-2 text-green-700 font-semibold text-sm"><Check className="w-4 h-4" /> Contraseña restablecida para {result.name}</div>
            <div className="bg-slate-900 text-white rounded-xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Contraseña nueva</div>
                <div className="font-mono text-2xl font-bold tracking-wider" data-testid="temp-password">{result.temp_password}</div>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(result.temp_password); toast.success("Contraseña copiada"); }} className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-testid="copy-temp-password" aria-label="Copiar">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500">Compartila con el cliente por un canal seguro. Esta contraseña no se vuelve a mostrar.</p>
            <div className="flex flex-wrap justify-end gap-2">
              {waPhone && (
                <a href={`https://wa.me/${waPhone}?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors" data-testid="send-password-whatsapp">
                  <MessageCircle className="w-4 h-4" /> Enviar por WhatsApp
                </a>
              )}
              <button onClick={onClose} className="btn-outline" data-testid="reset-done-btn">Listo</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
